import type { Defeat, DomainEvent, Victory } from "../../models/events.ts";
import { resolveWorldBalance } from "../../models/config/worldBalance.ts";
import type { ClanState, TileKey, WorldState } from "../../models/worldState.ts";
import { tileKey } from "../../models/worldState.ts";
import type { ReduceContext } from "../context.ts";
import { applyEndTurnTileFood } from "../economy/tileEconomy.ts";
import { resolveEndTurnClan } from "../economy/turnEngine.ts";
import { applyRandomEventTurnEnd } from "../events/applyRandomEventTurnEnd.ts";
import { applyMapTimelineBeats } from "../map/applyMapTimeline.ts";
import { planTurnAdvancement } from "../map/planTurnAdvancement.ts";
import type { ReduceResult } from "../result.ts";
import { resolveAmbientWeather } from "../world/resolveAmbientWeather.ts";
import { getSeasonForWeatherTurn } from "../world/weatherCalendar.ts";

function finishWithOutcome(
  state: WorldState,
  phase: "victory" | "defeat",
  event: Victory | Defeat,
  tiles: WorldState["tiles"],
  clan: ClanState,
): ReduceResult {
  return {
    state: {
      ...state,
      phase,
      tiles,
      clan,
    },
    events: [event],
  };
}

function clanTileKey(state: WorldState): TileKey {
  return tileKey(state.clan.currentTile.q, state.clan.currentTile.r);
}

/**
 * End-turn food economy, growth/famine, season/weather, random events, and map timeline beats.
 */
export function reduceEndTurn(
  state: WorldState,
  context: ReduceContext = {},
): ReduceResult {
  const balance = resolveWorldBalance(context.balance);
  const stayedOnTile = !state.clan.movedThisTurn;
  const { tiles, foodProduction } = applyEndTurnTileFood(
    state.tiles,
    state.clan.currentTile,
    stayedOnTile,
    balance,
  );

  if (foodProduction === null) {
    return { state, events: [] };
  }

  const populationBefore = state.clan.population;
  const turnResult = resolveEndTurnClan(state.clan, foodProduction, balance);

  if (turnResult.outcome === "victory") {
    return finishWithOutcome(state, "victory", { type: "Victory" }, tiles, turnResult.clan);
  }

  if (turnResult.outcome === "defeat") {
    return finishWithOutcome(state, "defeat", { type: "Defeat" }, tiles, turnResult.clan);
  }

  const playingTurnEnded = state.turn;
  const nextTurn = state.turn + 1;
  const previousSeason = state.season;
  const nextSeason = getSeasonForWeatherTurn(nextTurn);
  const clanTile = tiles[clanTileKey(state)];
  const ambientWeather = clanTile
    ? resolveAmbientWeather(nextTurn, clanTile.terrain)
    : turnResult.clan.ambientWeather;

  const events: DomainEvent[] = [];
  if (previousSeason !== nextSeason) {
    events.push({
      type: "SeasonChanged",
      from: previousSeason,
      to: nextSeason,
    });
  }

  const plan = planTurnAdvancement(
    context.mapScenario,
    playingTurnEnded,
    nextTurn,
  );

  let midState: WorldState = {
    ...state,
    turn: nextTurn,
    season: nextSeason,
    tiles,
    clan: {
      ...turnResult.clan,
      movedThisTurn: false,
      ambientWeather,
    },
  };

  const randomEventResult = applyRandomEventTurnEnd(
    midState,
    { allowNewActivations: plan.allowNewActivations },
    balance,
  );
  events.push(...randomEventResult.events);

  midState = {
    ...midState,
    randomEvent: randomEventResult.randomEvent,
    rng: randomEventResult.rng,
  };

  if (plan.beatsForEndedTurn.length > 0) {
    const timelineResult = applyMapTimelineBeats(
      midState,
      plan.beatsForEndedTurn,
      playingTurnEnded,
      plan.activationTurn,
      context.timelineStrings,
      balance,
    );
    midState = timelineResult.state;
    events.push(...timelineResult.events);
  }

  const revealedTileCount = Object.values(midState.tiles).filter(
    (entry) => entry.visibility === "revealed",
  ).length;
  events.push({
    type: "TurnEnded",
    turn: nextTurn,
    populationBefore,
    populationAfter: midState.clan.population,
    revealedTileCount,
    stayedOnTile,
    foodProduction,
    terrain: clanTile?.terrain ?? state.tiles[clanTileKey(state)]!.terrain,
  });

  return {
    state: midState,
    events,
  };
}
